import cookies from '../node_modules/@buzzfeed/bf-utils/lib/cookies/index.js';
import uuid4 from '../node_modules/@buzzfeed/bf-utils/lib/uuid4/index.js';

/**
 * Processes the string to
 * - Remove disallowed characters
 * - Replace spaces with an underscore
 * - Convert case to lowercase
 * @param {String} - dirty string
 * @return {String} - processed "cleaned up" string
 */
const cleanString = input => {
  // https://regex101.com/r/stkYZw/3
  // https://regex101.com/r/m3kM50/2
  return String(input).replace(/[^\w\s-_/|]/g, '').replace(/\s+/g, '_').toLowerCase();
};

/**
 * Additional checking Source fields
 * @param [items]
 */
const cleanSourceFields = items => items.map(item => item === 'undefined' ? '' : item);

/**
 * Takes an input and returns an integer representation. A value of `null` will be returned if the
 * input can not be converted.
 * @param  {any} num - Input to convert to integer
 * @return {integer|Null}
 */
const convertToInteger = num => num === null || isNaN(Number(num)) ? null : Number(num);

/**
 * Check if input is an array and return it, otherwise return an empty array.
 * @param  {any} data
 * @return {Array}
 */
const getArray = function (data) {
  let default_value = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return data && Array.isArray(data) ? data : default_value;
};

/**
 * Generates a data object by evaluating all supplied data layers.
 * All layers are always represented as an append-only list.
 */
const getDataFromLayers = async _ref => {
  let {
    context,
    layers = []
  } = _ref;
  let data_obj = {};
  for (let i = 0; i < layers.length; i++) {
    let layer = layers[i];
    if (typeof layer === 'function') {
      layer = await layer(context);
    }
    data_obj = {
      ...data_obj,
      ...layer
    };
  }
  return data_obj;
};

/**
 * Coordinates linking client_uuid with AMP events when a visitor moves from viewing a non-AMP page into viewing an AMP page.
 * - Gets the AMP client ID from cookie if it exists (will exist if user first visits AMP version of page)
 * - If the AMP client ID is not set, generate the AMP client ID and set the cookie.
 * @param  {String} options.client_id_cookie_name - dev, stage, prod
 * @param  {Number} options.expires - Cookie expiration. Defaults to 1 year to match expiration of cookie set if hitting AMP first
 * @return {String}
 */
const getAmpClientId = function () {
  let {
    client_id_cookie_name = '_amp_pd',
    expires = 365
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let client_id = cookies.get(client_id_cookie_name);
  if (!client_id) {
    /**
     * Will get the top level domain for any environment (dev, stage, prod). Examples:
     * https://tasty-ui.dev.buzzfeed.io       -> buzzfeed.io
     * https://tasty-ui.sso.stage.buzzfeed.io -> buzzfeed.io
     * https://stage.tasty.co                 -> tasty.co
     * https://tasty.co                       -> tasty.co
     * https://stage.buzzfeed.com             -> buzzfeed.com
     * https://www.buzzfeed.com               -> buzzfeed.com
     * @type {String}
     */
    const top_level_domain = window.location.hostname.split('.').splice(-2, 2).join('.');
    client_id = `amp-${uuid4()}`;
    cookies.set({
      name: client_id_cookie_name,
      value: client_id,
      days: expires,
      domain: top_level_domain
    });
  }
  return client_id;
};
class ClientEventSchemaLayerError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ClientEventSchemaLayerError';
  }
}
const isRequired = function () {
  let name = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  return new ClientEventSchemaLayerError(`Missing required field: "${name}"`);
};

/**
 * Removes all entries containing ClientEventSchemaLayerError instances from a given data object.
 * Also, if debug flag is true, warnings are logged to the console with the supplied Error.
 *
 * The reason that a warning is logged instead of throwing an error is because the data registry
 * API isn't versioned in the same way that our packages are. So in theory, someone could update
 * the data registry API to make a field not required, push out that change, and then we'd see JS
 * errors in services if the packages were not updated at the same time. With errors, if uncaught,
 * might crash out other page JS, and we don't want a bad tracking event to block the overall user
 * experience! Warnings would give us the same debugging power with less potential negative impact.
 * @param {Object} data - a data object produced by evaluating all data layers into a flat object (see `getDataFromLayers`).
 * @param {boolean} options.debug - Flag for determining if warnings should be logged to console
 * @return {Object}
 */
const validateLayerData = function (data) {
  let {
    debug = false
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const valid_entries = Object.entries(data).filter(_ref2 => {
    let [, value] = _ref2;
    if (value instanceof ClientEventSchemaLayerError) {
      // Only when debug is true (dev and stage) we output the errors as warnings to the console
      debug && console.warn(value);
      return false;
    }
    return true;
  });

  /**
   * Note: while Object.fromEntries would simplify this code, it's only supported in Node >= 12
   */
  const event_data = valid_entries.reduce((object_from_entries, _ref3) => {
    let [key, value] = _ref3;
    object_from_entries[key] = value;
    return object_from_entries;
  }, {});
  return event_data;
};

/**
 * A fast and simple 53-bit string hash function with low collision percentage. The function was
 * taken from the below stackoverflow answer.
 * @see https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/52171480#52171480
 * @param {string} str
 * @param {*} seed
 * @returns {number} - 53-bit integer hash (16 character long)
 */
const generateStringHash53Bit = function (str) {
  let seed = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ h1 >>> 16, 2246822507);
  h1 ^= Math.imul(h2 ^ h2 >>> 13, 3266489909);
  h2 = Math.imul(h2 ^ h2 >>> 16, 2246822507);
  h2 ^= Math.imul(h1 ^ h1 >>> 13, 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export { cleanSourceFields, cleanString, convertToInteger, generateStringHash53Bit, getAmpClientId, getArray, getDataFromLayers, isRequired, validateLayerData };
